import React, { useState, createContext, useContext, useEffect } from 'react';
import { Button } from '../Button';
import { cn } from '../../utils/cn';

interface SelectContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedLabel?: string;
  setSelectedLabel?: (label: string) => void;
  itemLabels: Map<string, string>;
  setItemLabel: (value: string, label: string) => void;
}

const SelectContext = createContext<SelectContextValue>({
  open: false,
  setOpen: () => {},
  itemLabels: new Map(),
  setItemLabel: () => {},
});

interface SelectProps {
  children: React.ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
}

const Select = ({ children, value, onValueChange, defaultValue }: SelectProps) => {
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [itemLabels] = useState(new Map<string, string>());
  
  const currentValue = value !== undefined ? value : internalValue;
  
  const setItemLabel = (itemValue: string, label: string) => {
    itemLabels.set(itemValue, label);
    // If this is the current value, update the selected label
    if (itemValue === currentValue) {
      setSelectedLabel(label);
    }
  };
  
  // Update selected label when value changes
  useEffect(() => {
    if (currentValue && itemLabels.has(currentValue)) {
      setSelectedLabel(itemLabels.get(currentValue) || '');
    } else if (!currentValue) {
      setSelectedLabel('');
    }
  }, [currentValue, itemLabels]);
  
  const handleValueChange = (newValue: string) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
    setOpen(false);
  };

  return (
    <SelectContext.Provider value={{
      value: currentValue,
      onValueChange: handleValueChange,
      open,
      setOpen,
      selectedLabel,
      setSelectedLabel,
      itemLabels,
      setItemLabel,
    }}>
      <div className="relative">
        {children}
      </div>
    </SelectContext.Provider>
  );
};

interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ children, className, ...props }, ref) => {
    const { open, setOpen } = useContext(SelectContext);
    
    return (
      <Button
        ref={ref}
        variant="outline"
        className={cn(
          'w-full justify-between text-left font-normal',
          className
        )}
        onClick={() => setOpen(!open)}
        {...props}
      >
        {children}
        <svg
          className="h-4 w-4 opacity-50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 9l6 6 6-6"
          />
        </svg>
      </Button>
    );
  }
);
SelectTrigger.displayName = 'SelectTrigger';

interface SelectValueProps {
  placeholder?: string;
}

const SelectValue = ({ placeholder }: SelectValueProps) => {
  const { value, selectedLabel } = useContext(SelectContext);
  
  return <span>{selectedLabel || value || placeholder}</span>;
};

interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
}

const SelectContent = ({ children, className }: SelectContentProps) => {
  const { open } = useContext(SelectContext);
  
  if (!open) return null;
  
  return (
    <div
      className={cn(
        'absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg',
        'dark:bg-gray-800 dark:border-gray-700',
        className
      )}
    >
      <div className="py-1">
        {children}
      </div>
    </div>
  );
};

interface SelectItemProps {
  children: React.ReactNode;
  value: string;
  className?: string;
}

const SelectItem = ({ children, value, className }: SelectItemProps) => {
  const { onValueChange, value: selectedValue, setSelectedLabel, setItemLabel } = useContext(SelectContext);
  
  const isSelected = selectedValue === value;
  const label = children?.toString() || value;
  
  // Register this item's label
  React.useEffect(() => {
    setItemLabel(value, label);
  }, [value, label, setItemLabel]);
  
  const handleSelect = () => {
    onValueChange?.(value);
    setSelectedLabel?.(label);
  };
  
  return (
    <button
      className={cn(
        'w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700',
        'focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none',
        isSelected && 'bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
        className
      )}
      onClick={handleSelect}
    >
      {children}
    </button>
  );
};

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
